/* eslint-disable camelcase */
import React, { useState } from "react"
import { Box, Typography, Paper, Tabs, Tab } from "@mui/material"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { useNavigate } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import Swal from "sweetalert2"

import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import { api } from "../../api"

import AssignmentIndIcon from "@mui/icons-material/AssignmentInd"
import FolderOpenIcon from "@mui/icons-material/FolderOpen"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate" // Asumiendo que usas esta utilidad de tu arquitectura

// Estilos base estandarizados
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(2),
  flexGrow: 1,
  height: "100%",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

function TabPanel(props) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`crm-tabpanel-${index}`}
      aria-labelledby={`crm-tab-${index}`}
      {...other}
      style={{ height: "100%" }}
    >
      {value === index && <Box sx={{ pt: 2, height: "100%" }}>{children}</Box>}
    </div>
  )
}

const CRM_Bandejas = () => {
  const [tabValue, setTabValue] = useState(0)
  const qc = useQueryClient()
  const navigate = useNavigate()

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // --- Mutación para Asignarse la Carpeta ---
  const { mutateAsync: asignarOportunidad } = useMutation({
    mutationFn: async (data) => {
      const response = await api.post("/CRM_Carpetas/asignarCarpeta", data)
      return response.data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["getOportunidadesPendientes"] })
      await qc.invalidateQueries({ queryKey: ["getMisCarpetas"] })
      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Oportunidad asignada. Ya puedes verla en Mis Carpetas.",
        confirmButtonColor: "#196C87",
      })
    },
    onError: (err) => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Ocurrió un error al asignar la oportunidad.",
        confirmButtonColor: "#196C87",
      })
    },
  })

  const handleAsignar = async (row) => {
    const confirmacion = await Swal.fire({
      title: "¿Asignarse esta oportunidad?",
      html: `<p>Paciente: <strong>${row.original.paciente}</strong></p><p>Servicio: ${row.original.artdescri}</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, tomar caso",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#196C87",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    })

    if (confirmacion.isConfirmed) {
      await asignarOportunidad({
        loccodigo: row.original.loccodigo,
        solcodigo: row.original.solcodigo,
        secingreso: row.original.secingreso,
      })
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión Comercial y Oportunidades</b>
        </div>

        <Box className={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 2, borderRadius: 2, height: "calc(100vh - 160px)", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tabValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
                <Tab label="Oportunidades Pendientes" sx={{ fontWeight: "bold" }} />
                <Tab label="Mis Carpetas / Auditoría" sx={{ fontWeight: "bold" }} />
              </Tabs>
            </Box>

            {/* PESTAÑA 0: BANDEJA DE ENTRADA */}
            <TabPanel value={tabValue} index={0}>
              <CustomConditionalActionsTableServer
                endpoint="/CRM_Carpetas/getOportunidadesPendientes"
                errorMsgFilterSearch="Error al cargar oportunidades"
                queryKeyModal="getOportunidadesPendientes"
                perPage={10}
                rowActionsWidthTable={100}
                rowActions={(row) => [
                  {
                    label: "Asignarme",
                    key: "ASIGNAR",
                    icon: <AssignmentIndIcon color="primary" />,
                    onClick: (row) => handleAsignar(row),
                  },
                ]}
                columnsTable={[
                  {
                    accessorKey: "histosolfecha",
                    header: "Fec. Emisión",
                    size: 140,
                    Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
                  },
                  {
                    accessorKey: "solcodigo",
                    header: "N° Solicitud",
                    size: 130,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "paciente",
                    header: "Paciente",
                    size: 250,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "histosoltipo",
                    header: "Tipo ERP",
                    size: 100,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "artdescri",
                    header: "Servicio / Artículo",
                    size: 200,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "medmedico",
                    header: "Médico Deriva",
                    size: 200,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                ]}
              />
            </TabPanel>

            {/* PESTAÑA 1: MIS CARPETAS ACTIVAS */}
            <TabPanel value={tabValue} index={1}>
              <CustomConditionalActionsTableServer
                endpoint="/CRM_Carpetas/getMisCarpetas"
                errorMsgFilterSearch="Error al cargar carpetas"
                queryKeyModal="getMisCarpetas"
                perPage={10}
                rowActionsWidthTable={100}
                rowActions={(row) => [
                  {
                    label: "Abrir Expediente",
                    key: "ABRIR",
                    icon: <FolderOpenIcon color="secondary" />,
                    onClick: (row) =>
                      navigate("/home/dashboard/CRM_Workspace", { state: { car_id: row.original.car_id } }),
                  },
                ]}
                columnsTable={[
                  {
                    accessorKey: "car_id",
                    header: "ID Carpeta",
                    size: 130,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "car_fecha_creacion",
                    header: "Fec. Asignación",
                    size: 140,
                    Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
                  },
                  {
                    accessorKey: "clicodigo",
                    header: "Cod. Paciente",
                    size: 120,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "pro_nombre",
                    header: "Proceso",
                    size: 150,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "eta_nombre",
                    header: "Etapa Actual",
                    size: 180,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "usr_asignado",
                    header: "Responsable",
                    size: 120,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "car_status",
                    header: "Estado Global",
                    size: 120,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                  {
                    accessorKey: "eta_sla_horas",
                    header: "SLA (H)",
                    size: 90,
                    Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                  },
                ]}
              />
            </TabPanel>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CRM_Bandejas
